import React, { useEffect, useState } from "react";
import Header from "./layers/Header";
import Footer from "./layers/Footer";
import { motion } from "framer-motion";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  BriefcaseMedical,
  CheckCircle2,
  Clock3,
  CreditCard,
  HeartPulse,
  MessageCircle,
  PhoneCall,
  ShieldCheck,
  Sparkles,
  Star,
  Stethoscope,
  Users,
  WalletCards,
  Zap,
} from "lucide-react";

const WHATSAPP_SUPPORT = "https://wa.me/2348077417209";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.55,
      ease: "easeOut",
    },
  }),
};

const Home = () => {
  const [reviews, setReviews] = useState([]);

  const navigate = useNavigate();

  const fetchReviews = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/reviews/display-true`
      );

      if (res.data.success) {
        setReviews(res.data.reviews || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  useEffect(() => {
    const doctorData = localStorage.getItem("doctor");

    if (doctorData) {
      const doctor = JSON.parse(doctorData);

      if (doctor?.email) {
        navigate("/dashboard/home");
      }
    }
  }, [navigate]);

  const steps = [
    {
      icon: MessageCircle,
      title: "Patients start on WhatsApp",
      desc: "Patients send complaints from WhatsApp. No app download, no friction.",
    },
    {
      icon: Users,
      title: "Cases arrive in your portal",
      desc: "You see assigned patients, complaints, history, and messages clearly.",
    },
    {
      icon: Stethoscope,
      title: "Consult and guide care",
      desc: "Chat, request details, review symptoms, prescribe, or refer when needed.",
    },
    {
      icon: CreditCard,
      title: "Earn from consultations",
      desc: "Paid access and subscription flows are handled inside the platform.",
    },
  ];

  const features = [
    {
      icon: MessageCircle,
      title: "WhatsApp-first workflow",
      desc: "Patients stay on WhatsApp while doctors manage care from a structured dashboard.",
    },
    {
      icon: BriefcaseMedical,
      title: "Clinical context in one place",
      desc: "See patient details, complaints, history, messages, investigations, and medications.",
    },
    {
      icon: Clock3,
      title: "Flexible doctor access",
      desc: "Consult remotely and manage patients without being tied to one physical location.",
    },
    {
      icon: ShieldCheck,
      title: "Secure doctor dashboard",
      desc: "Designed for verified doctors with controlled access to patient conversations.",
    },
    {
      icon: WalletCards,
      title: "Built-in monetization",
      desc: "Supports paid consultations, subscriptions, and future corporate healthcare workflows.",
    },
    {
      icon: HeartPulse,
      title: "Continuity of care",
      desc: "Patients can return, follow up, and continue their care through the same WhatsApp thread.",
    },
  ];

  const pricingCards = [
    {
      title: "Free Patient Access",
      price: "Text-based",
      desc: "Patients can start conversations and submit basic complaints from WhatsApp.",
      items: ["Text messages", "Patient onboarding", "Doctor review queue"],
    },
    {
      title: "Single Consultation",
      price: "₦2,500",
      desc: "Paid access for patients who need direct doctor support for a short care window.",
      items: ["24-hour access", "Doctor response", "Follow-up within window"],
      highlight: true,
    },
    {
      title: "Monthly Access",
      price: "₦10,000",
      desc: "For patients who want ongoing access to doctor consultations and care support.",
      items: ["30-day access", "More complete care", "Better continuity"],
    },
  ];

  return (
    <>
      <Header />

      <main className="zv-home-page">
      {/* ================= HERO ================= */}
      <section
        id="for-doctors"
        className="relative overflow-hidden bg-[#f6fbf8] pt-28"
      >
        <div className="absolute -left-32 top-10 h-96 w-96 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="absolute -right-32 top-40 h-96 w-96 rounded-full bg-cyan-200/40 blur-3xl" />
        <div className="absolute bottom-0 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-[#25D366]/10 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-12 sm:px-6 lg:grid-cols-2 lg:px-8 lg:pb-28 lg:pt-16">
          <motion.div initial="hidden" animate="visible">
            

            <motion.h1
              custom={2}
              variants={fadeUp}
              className="mt-6 max-w-3xl text-5xl font-black leading-[0.98] tracking-[-0.06em] text-slate-950 sm:text-6xl lg:text-7xl"
            >
              Practice medicine
              <span className="block text-[#075E54]">from anywhere.</span>
            </motion.h1>

            <motion.p
              custom={3}
              variants={fadeUp}
              className="mt-6 max-w-xl text-base font-medium leading-8 text-slate-600 sm:text-lg"
            >
              Join Zaviramed and consult patients who start from WhatsApp.
              Manage messages, clinical context, prescriptions, referrals, and
              paid access from one clean doctor dashboard.
            </motion.p>

            <motion.div
              custom={4}
              variants={fadeUp}
              className="mt-8 flex flex-col gap-3 sm:flex-row"
            >
              <button
                onClick={() => navigate("/register-as-doctor")}
                className="group inline-flex h-14 items-center justify-center gap-2 rounded-2xl bg-[#075E54] px-7 text-sm font-black text-white shadow-[0_20px_45px_rgba(7,94,84,0.25)] transition hover:-translate-y-0.5 hover:bg-emerald-700"
              >
                Join as Doctor
                <ArrowRight size={17} className="transition group-hover:translate-x-1" />
              </button>

              <button
                onClick={() => {
                  const el = document.getElementById("how-it-works");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex h-14 items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-white px-7 text-sm font-black text-slate-800 shadow-sm transition hover:bg-emerald-50"
              >
                See how it works
              </button>
            </motion.div>

            <motion.div
              custom={5}
              variants={fadeUp}
              className="mt-8 grid max-w-xl grid-cols-3 gap-3"
            >
              {[
                { value: "WhatsApp", label: "Patient entry" },
                { value: "24/7", label: "Message history" },
                { value: "Secure", label: "Doctor portal" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-3xl border border-emerald-100 bg-white/80 p-4 shadow-sm backdrop-blur"
                >
                  <p className="text-sm font-black text-slate-950 sm:text-base">
                    {item.value}
                  </p>
                  <p className="mt-1 text-[11px] font-bold text-slate-500">
                    {item.label}
                  </p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* HERO VISUAL */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="relative"
          >
           

            <div className="rounded-[2rem] border border-emerald-100 bg-white p-3 shadow-[0_30px_80px_rgba(15,23,42,0.12)]">
              <div className="overflow-hidden rounded-[1.6rem] bg-[#eaf7ef]">
                <div className="flex items-center justify-between bg-[#075E54] px-5 py-4 text-white">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full ">
                    <img
                src="/zavira_nobg.png"
                alt="Zaviramed"
                className="h-9 w-9 object-contain transition duration-300 group-hover:scale-105"
              />

                    </div>
                    <div>
                      <p className="text-sm" style={{color: 'white'}}>Doctor Dashboard</p>
                      <p className="text-xs" style={{color: 'white'}}>Online now</p>
                    </div>
                  </div>
                  <BadgeCheck size={21} />
                </div>

                <div className="space-y-3 p-5">
                  <div className="max-w-[82%] rounded-2xl rounded-tl-sm bg-white p-4 shadow-sm">
                    <p className="text-sm font-bold text-slate-800">
                      Good morning doctor, I have had chest discomfort since
                      yesterday...
                    </p>
                    <p className="mt-2 text-[11px] font-bold text-slate-400">
                      Patient • WhatsApp
                    </p>
                  </div>

                  <div className="ml-auto max-w-[82%] rounded-2xl rounded-tr-sm bg-[#dcf8c6] p-4 shadow-sm">
                    <p className="text-sm font-bold text-slate-800">
                      I’ll ask a few questions first. Is the pain severe or
                      associated with difficulty breathing?
                    </p>
                    <p className="mt-2 text-right text-[11px] font-bold text-slate-500">
                      Doctor
                    </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {[
                      {
                        icon: HeartPulse,
                        title: "Clinical context",
                        text: "Age, sex, history and complaint.",
                      },
                      {
                        icon: Zap,
                        title: "Quick actions",
                        text: "Prescribe, refer, or continue care.",
                      },
                    ].map((item) => {
                      const Icon = item.icon;

                      return (
                        <div
                          key={item.title}
                          className="rounded-3xl border border-white/70 bg-white/80 p-4"
                        >
                          <Icon size={19} className="text-emerald-700" />
                          <p className="mt-2 text-sm font-black text-slate-950">
                            {item.title}
                          </p>
                          <p className="mt-1 text-xs font-semibold leading-relaxed text-slate-500">
                            {item.text}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section id="how-it-works" className="bg-white py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-black uppercase tracking-[0.18em] text-emerald-600">
              How it works
            </p>
            <h2 className="mt-3 text-4xl font-black tracking-[-0.04em] text-slate-950 sm:text-5xl">
              From WhatsApp message to clinical action.
            </h2>
            <p className="mt-4 text-base font-medium leading-8 text-slate-600">
              Zaviramed handles the patient entry point so doctors can focus on
              structured care.
            </p>
          </div>

          <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, index) => {
              const Icon = step.icon;

              return (
                <motion.div
                  key={step.title}
                  whileHover={{ y: -8 }}
                  className="rounded-[1.75rem] border border-emerald-100 bg-[#f8fcfa] p-6 shadow-sm transition hover:shadow-xl"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#25D366]/15 text-[#075E54]">
                    <Icon size={22} />
                  </div>

                  <p className="mt-6 text-xs font-black uppercase tracking-[0.18em] text-emerald-600">
                    Step {index + 1}
                  </p>

                  <h3 className="mt-2 text-lg font-black text-slate-950">
                    {step.title}
                  </h3>

                  <p className="mt-3 text-sm font-medium leading-7 text-slate-600">
                    {step.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= FEATURES ================= */}
      <section
        className="relative overflow-hidden bg-[#f6fbf8] py-24 sm:py-28"
        id='feature'
      >
        <div className="absolute -left-32 top-20 h-96 w-96 rounded-full bg-emerald-200/35 blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="features-heading-grid grid gap-12 lg:items-end">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.18em] text-emerald-600">
                Features
              </p>
              <h2 className="mt-3 text-4xl font-black tracking-[-0.04em] text-slate-950 sm:text-5xl">
                A doctor portal built around real patient conversations.
              </h2>
            </div>

            <p className="max-w-2xl text-base font-medium leading-8 text-slate-600">
              The new portal direction should feel like WhatsApp, but with the
              structure doctors need: patient profile, messages, notes,
              prescriptions, payment status, and follow-up context.
            </p>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => {
              const Icon = feature.icon;

              return (
                <motion.div
                  key={feature.title}
                  whileHover={{ y: -6 }}
                  className="rounded-[1.75rem] border border-emerald-100 bg-white p-6 shadow-sm transition hover:shadow-xl"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                    <Icon size={22} />
                  </div>

                  <h3 className="mt-5 text-lg font-black text-slate-950">
                    {feature.title}
                  </h3>

                  <p className="mt-3 text-sm font-medium leading-7 text-slate-600">
                    {feature.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section id="pricing" className="bg-white py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-black uppercase tracking-[0.18em] text-emerald-600">
              Pricing flow
            </p>
            <h2 className="mt-3 text-4xl font-black tracking-[-0.04em] text-slate-950 sm:text-5xl">
              Simple patient access, better doctor workflow.
            </h2>
            <p className="mt-4 text-base font-medium leading-8 text-slate-600">
              Zaviramed supports free entry, paid consultations, and recurring
              subscription access.
            </p>
          </div>

          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {pricingCards.map((card) => (
              <div
                key={card.title}
                className={`rounded-[2rem] border p-7 shadow-sm ${
                  card.highlight
                    ? "border-[#075E54] bg-[#075E54] text-white shadow-[0_25px_60px_rgba(7,94,84,0.22)]"
                    : "border-emerald-100 bg-[#f8fcfa] text-slate-950"
                }`}
              >
                <h3 className="text-xl font-black">{card.title}</h3>
                <p
                  className={`mt-4 text-4xl font-black tracking-[-0.04em] ${
                    card.highlight ? "text-white" : "text-[#075E54]"
                  }`}
                >
                  {card.price}
                </p>
                <p
                  className={`mt-4 text-sm font-medium leading-7 ${
                    card.highlight ? "text-white/75" : "text-slate-600"
                  }`}
                >
                  {card.desc}
                </p>

                <div className="mt-6 space-y-3">
                  {card.items.map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <CheckCircle2
                        size={18}
                        className={
                          card.highlight ? "text-[#25D366]" : "text-emerald-600"
                        }
                      />
                      <span
                        className={`text-sm font-bold ${
                          card.highlight ? "text-white/90" : "text-slate-700"
                        }`}
                      >
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <section id="" className="bg-[#f6fbf8] py-24 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.18em] text-emerald-600">
              About Zaviramed
            </p>
            <h2 className="mt-3 text-4xl font-black tracking-[-0.04em] text-slate-950 sm:text-5xl">
              The doctor-first layer for WhatsApp healthcare.
            </h2>
            <p className="mt-6 text-base font-medium leading-8 text-slate-600">
              Zaviramed helps doctors consult patients remotely while keeping
              the patient experience simple. Patients message through WhatsApp;
              doctors work from a structured dashboard.
            </p>
            <p className="mt-4 text-base font-medium leading-8 text-slate-600">
              This makes remote care easier to manage, easier to scale, and more
              practical for real Nigerian healthcare workflows.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {[
                "No patient hunting",
                "Flexible remote care",
                "Structured patient flow",
                "Payment-ready consultations",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 rounded-2xl bg-white p-4 shadow-sm"
                >
                  <CheckCircle2 size={18} className="text-emerald-600" />
                  <span className="text-sm font-black text-slate-700">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[2rem] border border-emerald-100 bg-white p-7 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                <Sparkles size={22} />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-950">
                  Why doctors join
                </h3>
                <p className="text-sm font-semibold text-slate-500">
                  Better care flow, less operational stress.
                </p>
              </div>
            </div>

            <div className="mt-7 space-y-4">
              {[
                {
                  title: "Patient context is organized",
                  desc: "Doctors do not need to dig through scattered messages to understand the case.",
                },
                {
                  title: "Care can continue remotely",
                  desc: "Patients can follow up from the same WhatsApp thread.",
                },
                {
                  title: "Doctors can earn with flexibility",
                  desc: "Consult patients from anywhere and manage care through the portal.",
                },
              ].map((item) => (
                <div key={item.title} className="rounded-3xl bg-[#f8fcfa] p-5">
                  <h4 className="text-sm font-black text-slate-950">
                    {item.title}
                  </h4>
                  <p className="mt-2 text-sm font-medium leading-7 text-slate-600">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= CONTACT ================= */}
      <section id="contact" className="bg-white py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <p className="text-sm font-black uppercase tracking-[0.18em] text-emerald-600">
            Contact
          </p>
          <h2 className="mt-3 text-4xl font-black tracking-[-0.04em] text-slate-950 sm:text-5xl">
            Want to join Zaviramed?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base font-medium leading-8 text-slate-600">
            Reach us through WhatsApp, phone, or email. WhatsApp is usually the
            fastest.
          </p>

          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {[
              {
                title: "WhatsApp",
                desc: "Fastest way to reach the team.",
                action: "Chat on WhatsApp",
                onClick: () => window.open(WHATSAPP_SUPPORT, "_blank"),
                icon: MessageCircle,
              },
              {
                title: "Call",
                desc: "Speak directly with our team.",
                action: "0916 034 0807",
                onClick: () => window.open("tel:09160340807", "_self"),
                icon: PhoneCall,
              },
              {
                title: "Email",
                desc: "For support or partnership inquiries.",
                action: "support@zaviramed.com.ng",
                onClick: () => window.open("mailto:support@zaviramed.com.ng", "_self"),
                icon: ShieldCheck,
              },
            ].map((item) => {
              const Icon = item.icon;

              return (
                <motion.button
                  key={item.title}
                  type="button"
                  onClick={item.onClick}
                  whileHover={{ y: -6 }}
                  className="rounded-[1.75rem] border border-emerald-100 bg-[#f8fcfa] p-7 text-left shadow-sm transition hover:bg-emerald-50 hover:shadow-xl"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-emerald-700 shadow-sm">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-5 text-lg font-black text-slate-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm font-medium leading-7 text-slate-600">
                    {item.desc}
                  </p>
                  <p className="mt-5 text-sm font-black text-emerald-700">
                    {item.action}
                  </p>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= REVIEWS ================= */}
      {reviews.length > 0 && (
        <section className="overflow-hidden bg-[#f6fbf8] py-24 sm:py-28">
          <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
            <p className="text-sm font-black uppercase tracking-[0.18em] text-emerald-600">
              Reviews
            </p>
            <h2 className="mt-3 text-4xl font-black tracking-[-0.04em] text-slate-950 sm:text-5xl">
              What patients are saying
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base font-medium leading-8 text-slate-600">
              Real feedback from patients who have used Zaviramed.
            </p>

            <div className="relative mt-14">
              <motion.div
                className="flex gap-5"
                animate={{
                  x: ["0%", "-50%"],
                }}
                transition={{
                  duration: 28,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                {[...reviews, ...reviews].map((r, i) => (
                  <div
                    key={`${r?._id || i}-${i}`}
                    className="min-w-[300px] max-w-[330px] rounded-[1.75rem] border border-emerald-100 bg-white p-6 text-left shadow-sm"
                  >
                    <div className="mb-3 flex">
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <Star
                          key={idx}
                          size={17}
                          className={
                            idx < (r.rating || 5)
                              ? "fill-amber-400 text-amber-400"
                              : "text-slate-200"
                          }
                        />
                      ))}
                    </div>

                    <p className="line-clamp-5 text-sm font-medium leading-7 text-slate-700">
                      “{r.comment}”
                    </p>

                    <div className="mt-5 flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-sm font-black text-emerald-700">
                        {r.patient?.name
                          ?.split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2) || "A"}
                      </div>

                      <div>
                        <p className="text-sm font-black text-slate-950">
                          {r.patient?.name || "Anonymous"}
                        </p>
                        <p className="text-xs font-bold text-slate-400">
                          {r.patient?.age || ""}{" "}
                          {r.patient?.sex ? `• ${r.patient.sex}` : ""}
                        </p>
                      </div>
                    </div>

                    {r.doctor && (
                      <p className="mt-3 text-xs font-black text-emerald-600">
                        Consulted with Dr. {r.doctor.name}
                      </p>
                    )}
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* ================= CTA ================= */}
      <section className="bg-[#075E54] px-4 py-20 text-white sm:px-6">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-black tracking-[-0.04em] sm:text-5xl">
            Ready to start practicing smarter?
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-base font-medium leading-8 text-white/75">
            Join the Zaviramed doctor network and manage patient care from a
            modern WhatsApp-first dashboard.
          </p>

          <button
            onClick={() => navigate("/register-as-doctor")}
            className="mt-8 inline-flex h-14 items-center justify-center gap-2 rounded-2xl bg-white px-8 text-sm font-black text-[#075E54] shadow-xl transition hover:-translate-y-0.5"
          >
            Join as Doctor
            <ArrowRight size={17} />
          </button>
        </div>
      </section>
      </main>

      <Footer />
    </>
  );
};

export default Home;
